import dagre from 'dagre';
import type { Node, Edge } from 'reactflow';

export interface LayoutOptions {
  direction?: 'TB' | 'LR' | 'BT' | 'RL';
  nodeWidth?: number;
  nodeHeight?: number;
  ranksep?: number;
  nodesep?: number;
}

const defaultOptions: Required<LayoutOptions> = {
  direction: 'TB',
  nodeWidth: 200,
  nodeHeight: 100,
  ranksep: 80,
  nodesep: 40,
};

export function layoutGraph(
  nodes: Node[],
  edges: Edge[],
  options: LayoutOptions = {}
): { nodes: Node[]; edges: Edge[] } {
  if (nodes.length === 0) {
    return { nodes: [], edges: [] };
  }

  const opts = { ...defaultOptions, ...options };

  const dagreGraph = new dagre.graphlib.Graph();
  dagreGraph.setDefaultEdgeLabel(() => ({}));
  dagreGraph.setGraph({
    rankdir: opts.direction,
    ranksep: opts.ranksep,
    nodesep: opts.nodesep,
  });

  // Add nodes to dagre
  nodes.forEach((node) => {
    dagreGraph.setNode(node.id, {
      width: opts.nodeWidth,
      height: opts.nodeHeight,
    });
  });

  // Add edges to dagre
  edges.forEach((edge) => {
    dagreGraph.setEdge(edge.source, edge.target);
  });

  // Run layout algorithm
  dagre.layout(dagreGraph);

  // Apply positions to nodes
  const layoutedNodes = nodes.map((node) => {
    const nodeWithPosition = dagreGraph.node(node.id);
    return {
      ...node,
      position: {
        x: nodeWithPosition.x - opts.nodeWidth / 2,
        y: nodeWithPosition.y - opts.nodeHeight / 2,
      },
    };
  });

  return { nodes: layoutedNodes, edges };
}

export interface JobEvent {
  job_id: string;
  trace_id: string;
  span_id?: string;
  parent_span_id?: string;
  namespace: string;
  status: string;
  time: string;
  details?: string;
  [key: string]: unknown;
}

export interface SpanNode {
  id: string;
  spanId?: string;
  parentId?: string;
  status: string;
  startTime: string;
  endTime?: string;
  details?: string;
  namespace: string;
  jobId: string;
  traceId: string;
  children: SpanNode[];
  childCount: number;
  isJob: boolean;
}

export interface GraphData {
  nodes: Node[];
  edges: Edge[];
}

export interface HierarchicalGraphData {
  rootNodes: SpanNode[];
  allNodes: Map<string, SpanNode>;
}

/**
 * Build a hierarchical structure from job events
 * Creates a flow: JOB_START -> span nodes -> JOB_END
 */
export function buildHierarchicalGraph(events: JobEvent[]): HierarchicalGraphData {
  const allNodes = new Map<string, SpanNode>();
  const rootNodes: SpanNode[] = [];

  if (!events.length) {
    return { rootNodes, allNodes };
  }

  // Sort all events by time
  const sortedEvents = [...events].sort(
    (a, b) => new Date(a.time).getTime() - new Date(b.time).getTime()
  );

  // Job-level statuses
  const jobStatuses = new Set(['JOB_START', 'JOB_END', 'JOB_END_EXCEPTION']);

  // Find JOB_START and JOB_END events
  const jobStartEvent = sortedEvents.find((e) => e.status === 'JOB_START');
  const jobEndEvent = sortedEvents.find(
    (e) => e.status === 'JOB_END' || e.status === 'JOB_END_EXCEPTION'
  );

  // Collect step events (non-job events)
  const stepEvents = sortedEvents.filter((e) => !jobStatuses.has(e.status));

  // Create JOB_START node
  let jobStartNode: SpanNode | null = null;
  if (jobStartEvent) {
    jobStartNode = {
      id: 'job_start',
      spanId: jobStartEvent.span_id,
      parentId: undefined,
      status: 'JOB_START',
      startTime: jobStartEvent.time,
      endTime: undefined,
      details: jobStartEvent.details,
      namespace: jobStartEvent.namespace,
      jobId: jobStartEvent.job_id,
      traceId: jobStartEvent.trace_id,
      children: [],
      childCount: 0,
      isJob: true,
    };
    allNodes.set(jobStartNode.id, jobStartNode);
    rootNodes.push(jobStartNode);
  }

  // Create JOB_END node
  let jobEndNode: SpanNode | null = null;
  if (jobEndEvent) {
    jobEndNode = {
      id: 'job_end',
      spanId: jobEndEvent.span_id,
      parentId: undefined,
      status: jobEndEvent.status,
      startTime: jobEndEvent.time,
      endTime: undefined,
      details: jobEndEvent.details,
      namespace: jobEndEvent.namespace,
      jobId: jobEndEvent.job_id,
      traceId: jobEndEvent.trace_id,
      children: [],
      childCount: 0,
      isJob: true,
    };
    allNodes.set(jobEndNode.id, jobEndNode);
  }

  // Group step events by span_id
  const spanGroups = new Map<string, JobEvent[]>();
  stepEvents.forEach((event) => {
    const spanId = event.span_id || 'unknown';
    if (!spanGroups.has(spanId)) {
      spanGroups.set(spanId, []);
    }
    spanGroups.get(spanId)!.push(event);
  });

  // Create nodes for each span group, sorted by first event time
  const spanNodes: SpanNode[] = [];
  spanGroups.forEach((events, spanId) => {
    const firstEvent = events[0];
    const lastEvent = events[events.length - 1];

    // Determine the most significant status for display
    let displayStatus = firstEvent.status;
    const statusPriority: Record<string, number> = {
      DEBUG: 1,
      INFO: 2,
      WARN: 3,
      ERROR: 4,
      FATAL: 5,
      PROCESS_STEP_START: 2,
      PROCESS_STEP_END: 3,
    };
    let maxPriority = statusPriority[displayStatus] || 0;
    events.forEach((e) => {
      const priority = statusPriority[e.status] || 0;
      if (priority > maxPriority) {
        displayStatus = e.status;
        maxPriority = priority;
      }
    });

    // Get a meaningful detail
    const eventCount = events.length;
    let detail = `${eventCount} event${eventCount > 1 ? 's' : ''}`;
    for (const e of events) {
      if (e.details && e.details.length > 0 && e.details.length < 80) {
        detail = e.details;
        break;
      }
    }

    const spanNode: SpanNode = {
      id: `span:${spanId}`,
      spanId: spanId,
      parentId: undefined,
      status: displayStatus,
      startTime: firstEvent.time,
      endTime: lastEvent.time !== firstEvent.time ? lastEvent.time : undefined,
      details: detail,
      namespace: firstEvent.namespace,
      jobId: firstEvent.job_id,
      traceId: firstEvent.trace_id,
      children: [],
      childCount: eventCount,
      isJob: false,
    };

    allNodes.set(spanNode.id, spanNode);
    spanNodes.push(spanNode);
  });

  // Sort span nodes by start time
  spanNodes.sort(
    (a, b) => new Date(a.startTime).getTime() - new Date(b.startTime).getTime()
  );

  // Build the execution flow chain:
  // JOB_START -> span1 -> span2 -> ... -> JOB_END
  // We'll use children to represent the "next" node in the flow

  if (jobStartNode && spanNodes.length > 0) {
    // JOB_START's child is the first span
    jobStartNode.children.push(spanNodes[0]);
    jobStartNode.childCount = 1;
  }

  // Chain spans together
  for (let i = 0; i < spanNodes.length - 1; i++) {
    spanNodes[i].children.push(spanNodes[i + 1]);
    spanNodes[i].childCount = 1;
  }

  // Last span's child is JOB_END
  if (spanNodes.length > 0 && jobEndNode) {
    spanNodes[spanNodes.length - 1].children.push(jobEndNode);
    spanNodes[spanNodes.length - 1].childCount = 1;
  } else if (jobStartNode && jobEndNode && spanNodes.length === 0) {
    // No spans, JOB_START directly to JOB_END
    jobStartNode.children.push(jobEndNode);
    jobStartNode.childCount = 1;
  }

  // If no JOB_START, make the first span the root
  if (!jobStartNode && spanNodes.length > 0) {
    rootNodes.push(spanNodes[0]);
  }

  return { rootNodes, allNodes };
}

/**
 * Build visible graph based on expanded state
 * - When collapsed (expandedNodes is empty): Shows JOB_START -> JOB_END only
 * - When expanded: Shows full flow JOB_START -> spans -> JOB_END
 */
export function buildVisibleGraph(
  hierarchicalData: HierarchicalGraphData,
  expandedNodes: Set<string>
): GraphData {
  const nodes: Node[] = [];
  const edges: Edge[] = [];
  const addedNodes = new Set<string>();

  const isExpanded = expandedNodes.size > 0;

  // Find job_start and job_end nodes
  const jobStartNode = hierarchicalData.allNodes.get('job_start');
  const jobEndNode = hierarchicalData.allNodes.get('job_end');

  // Count span nodes (non-job nodes)
  let spanCount = 0;
  hierarchicalData.allNodes.forEach((node) => {
    if (!node.isJob) spanCount++;
  });

  // Helper to add a node
  function addNode(spanNode: SpanNode, depth: number = 0) {
    if (addedNodes.has(spanNode.id)) return;
    addedNodes.add(spanNode.id);

    // Determine label based on node type
    let label = spanNode.details || 'Step';
    if (spanNode.status === 'JOB_START') {
      label = 'Job Start';
    } else if (spanNode.status === 'JOB_END') {
      label = 'Job End';
    } else if (spanNode.status === 'JOB_END_EXCEPTION') {
      label = 'Job Failed';
    }

    // For job_start node, show how many spans are hidden when collapsed
    const hasHiddenChildren = spanNode.id === 'job_start' && !isExpanded && spanCount > 0;

    nodes.push({
      id: spanNode.id,
      type: spanNode.isJob ? 'jobNode' : 'stepNode',
      position: { x: 0, y: 0 },
      data: {
        label,
        status: spanNode.status,
        time: spanNode.startTime,
        endTime: spanNode.endTime,
        namespace: spanNode.namespace,
        jobId: spanNode.jobId,
        spanId: spanNode.spanId,
        traceId: spanNode.traceId,
        details: spanNode.details,
        hasChildren: hasHiddenChildren || spanNode.children.length > 0,
        isExpanded: isExpanded,
        childCount: hasHiddenChildren ? spanCount : spanNode.childCount,
        depth,
      },
    });
  }

  if (!isExpanded) {
    // Collapsed view: just JOB_START -> JOB_END
    if (jobStartNode) {
      addNode(jobStartNode, 0);
    }
    if (jobEndNode) {
      addNode(jobEndNode, 1);
    }
    if (jobStartNode && jobEndNode) {
      edges.push({
        id: 'job_start->job_end',
        source: 'job_start',
        target: 'job_end',
        animated: false,
        label: spanCount > 0 ? `${spanCount} steps` : undefined,
      });
    }
  } else {
    // Expanded view: show full flow
    function addNodeAndNext(spanNode: SpanNode, depth: number = 0) {
      addNode(spanNode, depth);

      // Add edges to children (next nodes in the flow)
      spanNode.children.forEach((child) => {
        edges.push({
          id: `${spanNode.id}->${child.id}`,
          source: spanNode.id,
          target: child.id,
          animated: child.status.includes('START'),
        });
        addNodeAndNext(child, depth + 1);
      });
    }

    // Start from root nodes
    hierarchicalData.rootNodes.forEach((rootNode) => {
      addNodeAndNext(rootNode, 0);
    });
  }

  // If multiple roots with no edges, create sequential edges
  if (edges.length === 0 && nodes.length > 1) {
    const sortedNodes = [...nodes].sort((a, b) => {
      const timeA = new Date(a.data.time as string).getTime();
      const timeB = new Date(b.data.time as string).getTime();
      return timeA - timeB;
    });

    for (let i = 0; i < sortedNodes.length - 1; i++) {
      edges.push({
        id: `${sortedNodes[i].id}->${sortedNodes[i + 1].id}`,
        source: sortedNodes[i].id,
        target: sortedNodes[i + 1].id,
        animated: false,
      });
    }
  }

  return layoutGraph(nodes, edges);
}

/**
 * Legacy function for backwards compatibility
 */
export function buildJobGraph(events: JobEvent[]): GraphData {
  const hierarchicalData = buildHierarchicalGraph(events);
  // By default, don't expand any nodes - show collapsed view
  return buildVisibleGraph(hierarchicalData, new Set());
}

export function getNodeStatusColor(status: string): string {
  switch (status) {
    case 'JOB_START':
      return '#1890ff'; // blue
    case 'PROCESS_STEP_START':
      return '#13c2c2'; // cyan
    case 'PROCESS_STEP_END':
      return '#52c41a'; // green
    case 'JOB_END':
      return '#52c41a'; // green
    case 'JOB_END_EXCEPTION':
    case 'ERROR':
    case 'FATAL':
      return '#ff4d4f'; // red
    case 'WARN':
      return '#faad14'; // orange
    case 'INFO':
      return '#1890ff'; // blue
    case 'DEBUG':
      return '#8c8c8c'; // gray
    default:
      return '#d9d9d9'; // gray
  }
}

export function getNodeStatusIcon(status: string): 'check' | 'close' | 'loading' | 'clock' {
  switch (status) {
    case 'JOB_END':
    case 'PROCESS_STEP_END':
      return 'check';
    case 'JOB_END_EXCEPTION':
      return 'close';
    case 'JOB_START':
    case 'PROCESS_STEP_START':
      return 'loading';
    default:
      return 'clock';
  }
}
